import { type FormInstance, Space } from "antd";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { FormDatePicker } from "~components/Form/FormDatePicker";
import { FormInput } from "~components/Form/FormInput";
import { FormSelect } from "~components/Form/FormSelect";
import { FormHelpInfoModal } from "~components/FormHelpInfoModal/FormHelpInfoModal.tsx";
import { CountryCodes } from "~enums/countryCodes.ts";
import { Genders } from "~enums/genders.ts";
import { PersonStatuses } from "~enums/personStatuses.ts";
import { Sports } from "~enums/sports.ts";
import { ManagePersonPhoto } from "~pages/Persons/PersonProfile/components/ManagePersonPhoto.tsx";
import type { UpsertPersonForm } from "~pages/Persons/PersonProfile/types/upsertPersonForm.ts";
import type { Person } from "~types/person.ts";

type Props = {
  data?: Person;
  isLoading: boolean;
  form: FormInstance<UpsertPersonForm>;
};

const fields = [
  {
    titleIntlKey: "name.local.full",
    descriptionIntlKey: "form.help.person.local.full.name",
  },
  {
    titleIntlKey: "name.latin.full",
    descriptionIntlKey: "form.help.person.latin.full.name",
  },
  {
    titleIntlKey: "abbreviated.name",
    descriptionIntlKey: "form.help.person.abbreviated.name",
  },
  { titleIntlKey: "date.of.birth", descriptionIntlKey: "form.help.person.dob" },
  { titleIntlKey: "deceased", descriptionIntlKey: "form.help.person.deceased" },
  { titleIntlKey: "gender", descriptionIntlKey: "form.help.person.gender" },
  {
    titleIntlKey: "nationality",
    descriptionIntlKey: "form.help.person.nationality",
  },
  {
    titleIntlKey: "knownAs",
    descriptionIntlKey: "form.help.person.latin.full.name",
  },
  {
    titleIntlKey: "name.family.local",
    descriptionIntlKey: "form.help.person.local.family.name",
  },
  {
    titleIntlKey: "name.family.latin",
    descriptionIntlKey: "form.help.person.latin.family.name",
  },
  {
    titleIntlKey: "name.given.local",
    descriptionIntlKey: "form.help.person.local.given.name",
  },
  {
    titleIntlKey: "name.given.latin",
    descriptionIntlKey: "form.help.person.latin.given.name",
  },
  { titleIntlKey: "status", descriptionIntlKey: "form.help.person.status" },
  {
    titleIntlKey: "person.id",
    descriptionIntlKey: "form.help.person.player.id",
  },
  {
    titleIntlKey: "externalId",
    descriptionIntlKey: "form.help.person.external.id",
  },
  { titleIntlKey: "added", descriptionIntlKey: "form.help.person.created" },
  { titleIntlKey: "updated", descriptionIntlKey: "form.help.person.updated" },
];

export const PersonProfileBasicSection = ({ data, isLoading, form }: Props) => {
  const { formatMessage } = useIntl();

  const { sport } = useParams();
  const isHockey = sport === Sports.Hockey;

  return (
    <div className="person-profile-form">
      <div className="person-profile-photo-container">
        <ManagePersonPhoto data={data} isLoading={isLoading} />
      </div>
      <div className="person-profile-columns">
        <div className="person-profile-basic-title-container">
          <div className="section-title">{formatMessage({ id: "basic.details" })}</div>
          <FormHelpInfoModal titleKey={"person.information"} fields={fields} />
        </div>
        <div className="form-grid">
          <FormInput<UpsertPersonForm>
            label={formatMessage({ id: "name.local.full" })}
            showSkeleton={isLoading}
            name="nameFullLocal"
            inputProps={{
              placeholder: formatMessage({ id: "name.local.full" }),
            }}
            rules={[
              {
                required: true,
                message: formatMessage(
                  {
                    id: "form.help.required.field",
                  },
                  {
                    field: formatMessage({
                      id: "name.local.full",
                    }),
                  },
                ),
              },
              {
                max: 200,
                message: formatMessage({ id: "form.validation.max.length" }, { length: 200 }),
              },
            ]}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "name.latin.full" })}
            name="nameFullLatin"
            inputProps={{
              placeholder: formatMessage({ id: "name.latin.full" }),
            }}
            rules={[
              {
                max: 200,
                message: formatMessage({ id: "form.validation.max.length" }, { length: 200 }),
              },
            ]}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "abbreviated.name" })}
            name="nameAbbreviated"
            inputProps={{
              placeholder: formatMessage({ id: "abbreviated.name" }),
            }}
            rules={[
              {
                max: 100,
                message: formatMessage({ id: "form.validation.max.length" }, { length: 100 }),
              },
            ]}
          />
          <FormDatePicker<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "date.of.birth" })}
            name="dob"
            datePickerProps={{ format: "DD/MM/YYYY" }}
          />
          <FormDatePicker<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "deceased" })}
            name="deceased"
            datePickerProps={{ format: "DD/MM/YYYY" }}
          />
          <FormSelect<UpsertPersonForm>
            showSkeleton={isLoading}
            name="gender"
            label={formatMessage({ id: "gender" })}
            rules={[
              {
                required: true,
                message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "gender" }) }),
              },
            ]}
            selectProps={{
              options: [Genders.Male, Genders.Female, Genders.Unknown].map((gender) => ({
                label: formatMessage({ id: `gender.${gender}` }),
                value: gender,
              })),
            }}
          />
          <FormSelect<UpsertPersonForm>
            showSkeleton={isLoading}
            name="nationality"
            label={formatMessage({ id: "nationality" })}
            rules={[
              {
                required: true,
                message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "nationality" }) }),
              },
            ]}
            selectProps={{
              showSearch: true,
              options: Object.values(CountryCodes).map((code) => ({
                label: formatMessage({ id: `countryCode.${code}` }),
                value: code,
              })),
              onChange: isHockey
                ? (value) => {
                    const additionalDetails = form.getFieldValue("additionalDetails") || {};
                    form.setFieldsValue({
                      additionalDetails: {
                        ...additionalDetails,
                        representation: value,
                      },
                    });
                  }
                : undefined,
            }}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "knownAs" })}
            name={["additionalNames", "default", "knownAs"]}
            inputProps={{ placeholder: formatMessage({ id: "knownAs" }) }}
            rules={[
              {
                max: 200,
                message: formatMessage({ id: "form.validation.max.length" }, { length: 200 }),
              },
            ]}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "name.family.local" })}
            name="nameFamilyLocal"
            inputProps={{
              placeholder: formatMessage({ id: "name.family.local" }),
            }}
            rules={[
              {
                max: 200,
                message: formatMessage({ id: "form.validation.max.length" }, { length: 200 }),
              },
            ]}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "name.family.latin" })}
            name="nameFamilyLatin"
            inputProps={{
              placeholder: formatMessage({ id: "name.family.latin" }),
            }}
            rules={[
              {
                max: 200,
                message: formatMessage({ id: "form.validation.max.length" }, { length: 200 }),
              },
            ]}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "name.given.local" })}
            name="nameGivenLocal"
            inputProps={{
              placeholder: formatMessage({ id: "name.given.local" }),
            }}
            rules={[
              {
                max: 200,
                message: formatMessage({ id: "form.validation.max.length" }, { length: 200 }),
              },
            ]}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={formatMessage({ id: "name.given.latin" })}
            name="nameGivenLatin"
            inputProps={{
              placeholder: formatMessage({ id: "name.given.latin" }),
            }}
            rules={[
              {
                max: 200,
                message: formatMessage({ id: "form.validation.max.length" }, { length: 200 }),
              },
            ]}
          />
          <FormSelect<UpsertPersonForm>
            showSkeleton={isLoading}
            name="status"
            label={formatMessage({ id: "status" })}
            rules={[
              {
                required: true,
                message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "status" }) }),
              },
            ]}
            selectProps={{
              options: [
                PersonStatuses.Active,
                PersonStatuses.Inactive,
                PersonStatuses.Pending,
                PersonStatuses.Deceased,
                PersonStatuses.Unregistered,
              ].map((status) => ({
                label: formatMessage({ id: `roleStatus.${status}` }),
                value: status,
              })),
            }}
          />
          <div />
          <div />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={
              <Space>
                {formatMessage({ id: "person.id" })}
                <i className="fas fa-xs fa-lock" />
              </Space>
            }
            name="personId"
            inputProps={{
              readOnly: true,
              style: { backgroundColor: "#EBEBEB", cursor: "not-allowed" },
              placeholder: formatMessage({ id: "person.id" }),
            }}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={
              <Space>
                {formatMessage({ id: "externalId" })}
                <i className="fas fa-xs fa-lock" />
              </Space>
            }
            inputProps={{
              readOnly: true,
              style: { backgroundColor: "#EBEBEB", cursor: "not-allowed" },
              placeholder: formatMessage({ id: "externalId" }),
            }}
            name="externalId"
          />
          <div />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={
              <Space>
                {formatMessage({ id: "created_at" })}
                <i className="fas fa-xs fa-lock" />
              </Space>
            }
            name="added"
            inputProps={{
              readOnly: true,
              style: { backgroundColor: "#EBEBEB", cursor: "not-allowed" },
            }}
          />
          <FormInput<UpsertPersonForm>
            showSkeleton={isLoading}
            label={
              <Space>
                {formatMessage({ id: "updated_at" })}
                <i className="fas fa-xs fa-lock" />
              </Space>
            }
            inputProps={{
              readOnly: true,
              style: { backgroundColor: "#EBEBEB", cursor: "not-allowed" },
            }}
            name="updated"
          />
        </div>
      </div>
    </div>
  );
};
